<script lang="ts" setup>
import { computed, ref, useId } from "vue";

import { useIsOffline } from "../../composables/useIsOffline";
import { useAuditStore } from "../../store";
import TiptapEditor from "./TiptapEditor.vue";

defineOptions({
  inheritAttrs: false
});

const props = defineProps<{
  modelValue: string | null;
  label: string;
  description: string;
  type: "criterium" | "notes";
}>();

defineEmits(["update:comment"]);

defineExpose({
  focusEditor: () => {
    richTextEditorRef.value?.focusEditor();
  }
});

const auditStore = useAuditStore();
const isOffline = useIsOffline();
const uniqueId = useId();

const richTextEditorRef = ref<InstanceType<typeof TiptapEditor>>();

const showLabel = computed(() => props.type === "notes");

const uploadSuccess = ref("");

// Announce upload success to screen readers
function announceUploadSuccess(fileName: string) {
  if (fileName === "external") {
    uploadSuccess.value = "L’image a été correctement insérée";
  } else {
    uploadSuccess.value = `L’image « ${fileName} » a été correctement insérée`;
  }

  setTimeout(() => {
    uploadSuccess.value = "";
  }, 3000);
}
</script>

<template>
  <p :id="`rich-text-editor-label-${uniqueId}`" class="fr-label" :class="showLabel ? 'fr-mb-1v' : 'fr-sr-only'">
    {{ label }}
  </p>
  <p class="fr-sr-only" aria-live="polite">{{ uploadSuccess }}</p>
  <p :id="`rich-text-editor-description-${uniqueId}`" class="fr-text--xs fr-mb-1w editor-description">
    {{ description }}
  </p>
  <TiptapEditor
    v-bind="$attrs"
    :key="`${uniqueId}-${auditStore.currentPageId}`"
    ref="richTextEditorRef"
    :model-value="modelValue"
    :labelled-by="`rich-text-editor-label-${uniqueId}`"
    :described-by="`rich-text-editor-description-${uniqueId} rich-text-editor-size-image-${uniqueId}`"
    :disabled="isOffline"
    @update:model-value="$emit('update:comment', $event)"
    @image:uploaded="announceUploadSuccess"
  />
  <p :id="`rich-text-editor-size-image-${uniqueId}`" class="fr-text--xs fr-mt-1w fr-mb-2w editor-description">Taille maximale par image : 2 Mo. Une seule image peut être ajoutée à la fois.</p>
</template>

<style scoped>
.editor-description {
  color: var(--text-mention-grey);
}
</style>
